import {memo} from 'react';
import type {CSSProperties, ElementType, ReactNode} from 'react';
import styles from './Grid.module.scss';

type GridSize = number | 'auto' | true;
type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface GridProps {
    children?: ReactNode;
    container?: boolean;
    item?: boolean;
    /** MUI spacing steps (8px each) between the container's direct items. */
    spacing?: number;
    xs?: GridSize;
    sm?: GridSize;
    md?: GridSize;
    lg?: GridSize;
    xl?: GridSize;
    alignItems?: CSSProperties['alignItems'];
    justifyContent?: CSSProperties['justifyContent'];
    direction?: CSSProperties['flexDirection'];
    component?: ElementType;
    className?: string;
    style?: CSSProperties;
    id?: string;
}

const BREAKPOINTS: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'];
const COLUMNS = 12;
const SPACING_UNIT = 8;

// MUI Grid's own rounding: Math.round((size / columns) * 10e7) / 10e5 %.
const percent = (size: number) => `${Math.round((size / COLUMNS) * 10e7) / 10e5}%`;

// The CSS custom properties one breakpoint's size resolves to (MUI v5 Grid item rules).
const sizeVars = (bp: Breakpoint, size: GridSize): Record<string, string> => {
    if (size === true)
        return {
            [`--g-basis-${bp}`]: '0',
            [`--g-grow-${bp}`]: '1',
            [`--g-max-${bp}`]: '100%',
            [`--g-shrink-${bp}`]: '1',
            [`--g-width-${bp}`]: 'auto'
        };
    if (size === 'auto')
        return {
            [`--g-basis-${bp}`]: 'auto',
            [`--g-grow-${bp}`]: '0',
            [`--g-max-${bp}`]: 'none',
            [`--g-shrink-${bp}`]: '0',
            [`--g-width-${bp}`]: 'auto'
        };
    const p = percent(size);
    return {[`--g-basis-${bp}`]: p, [`--g-grow-${bp}`]: '0', [`--g-max-${bp}`]: p, [`--g-shrink-${bp}`]: '1', [`--g-width-${bp}`]: 'auto'};
};

/**
 * Plain stand-in for MUI <Grid> (v5, 12 columns): same container/item box model, spacing gutters and breakpoint sizes,
 * without Emotion. Sizes travel as CSS custom properties (computed with MUI's rounding), so no class name is built at
 * runtime and fractional sizes like `lg={1.7}` work. As in MUI, a size applies from its breakpoint up until a larger
 * breakpoint overrides it; below the first size the item keeps the default flex sizing.
 */
const Grid = ({
    children,
    container = false,
    item = false,
    spacing = 0,
    xs,
    sm,
    md,
    lg,
    xl,
    alignItems,
    justifyContent,
    direction,
    component: Tag = 'div',
    className,
    style,
    id
}: GridProps) => {
    const sizes: Partial<Record<Breakpoint, GridSize>> = {xs, sm, md, lg, xl};
    const vars: Record<string, string> = {};
    let sized = false;
    let current: GridSize | undefined;
    for (const bp of BREAKPOINTS) {
        if (sizes[bp] !== undefined) current = sizes[bp];
        if (current !== undefined) {
            Object.assign(vars, sizeVars(bp, current));
            sized = true;
        }
    }
    if (container && spacing) vars['--g-gap'] = `${spacing * SPACING_UNIT}px`;

    const classes = [
        container && styles.container,
        container && spacing && styles.spaced,
        item && styles.item,
        item && sized && styles.sized,
        className
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <Tag className={classes || undefined} id={id} style={{...vars, alignItems, justifyContent, flexDirection: direction, ...style}}>
            {children}
        </Tag>
    );
};

export default memo(Grid);
